// src/components/roads/roadReportIntel.js
// --- UPDATE (Road Works Report table) -----------------------------------------
// Pure helpers that join, per road:
//   • the inventory road (status, condition, metadata.ml_* written by the ML segment run)
//   • the AI remaining-life prediction (roadAiPredictions.getRoadPrediction — same numbers
//     as the inventory "AI Prediction" column)
//   • the server roll-up from GET /roads/report-summary (segments, WOs, QA, inspections)
// into one row with an AI priority score, ranked reasons and the next best action.
// No React here — unit-testable and shared by the table + CSV export.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (roads v1.4): ai.ponding (live road ponding forecast) on each row + CSV column.
// --- END UPDATE
import { getRoadPrediction, riskLevel } from './roadAiPredictions';

const DAY_MS = 86400000;

/** Segment ML action codes (model.py road-pci-1.1) → short labels. */
export const ML_ACTION_LABEL = Object.freeze({
    routine_maintenance: 'Routine maintenance',
    preventive_crack_seal: 'Crack seal',
    mill_and_overlay: 'Mill & overlay',
    reconstruction: 'Reconstruction',
    surface_treatment: 'Surface treatment',
});

/** Next-action intents the page knows how to run. */
export const REPORT_INTENTS = Object.freeze({
    OPEN_REPORT: 'OPEN_REPORT',
    MAP: 'MAP',
    RUN_AI: 'RUN_AI',
});

export const EMPTY_SUMMARY = Object.freeze({
    segments: { count: 0, total_length_m: 0, avg_pci: null, min_pci: null, worst_segment_id: null, potholes: 0, poor_segments: 0, unscored_segments: 0, severe_cracking: 0, max_rutting_mm: null },
    work_orders: { total: 0, open: 0, critical_open: 0, overdue: 0, qa_pending: 0, last_created_at: null },
    qa: { audit_count: 0, avg_score: null, latest_status: null, rework_count: 0, last_audit_at: null },
    inspections: { submitted: 0, drafts: 0, last_score: null, last_rating: null, last_submitted_at: null },
});

const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

export const pciColor = (v) => (v == null ? 'gray' : v < 50 ? 'red' : v < 70 ? 'orange' : v < 85 ? 'yellow' : 'green');

/** Human "3d ago" / "in 5d" for a date (null-safe). */
export const relDays = (date, now = Date.now()) => {
    if (!date) return null;
    const t = new Date(date).getTime();
    if (!Number.isFinite(t)) return null;
    const d = Math.round((t - now) / DAY_MS);
    if (d === 0) return 'today';
    return d < 0 ? `${-d}d ago` : `in ${d}d`;
};

/**
 * Builds one report row.
 * @param {object} road     inventory road
 * @param {object} [summary] /roads/report-summary entry (null while loading)
 * @returns {object} row with ai / ml / segments / workOrders / qa / inspections / priority
 */
export function buildRoadReportRow(road, summary, { now = Date.now() } = {}) {
    const meta = road?.metadata || {};
    const s = summary || null;
    const seg = s?.segments || EMPTY_SUMMARY.segments;
    const wo = s?.work_orders || EMPTY_SUMMARY.work_orders;
    const qa = s?.qa || EMPTY_SUMMARY.qa;
    const insp = s?.inspections || EMPTY_SUMMARY.inspections;
    const prediction = getRoadPrediction(road);

    // ML: segment model results stored on the segments + the last run summary on the road
    const mlPci = num(meta.ml_pci) ?? seg.avg_pci;
    const ml = {
        pci: mlPci,
        minPci: seg.min_pci,
        engine: meta.ml_engine || null,                    // 'ml' | 'rules' | 'mixed'
        evaluatedAt: meta.ml_evaluated_at || null,
        action: meta.ml_recommended_action || null,
        actionLabel: ML_ACTION_LABEL[meta.ml_recommended_action] || (meta.ml_recommended_action ? String(meta.ml_recommended_action).replace(/_/g, ' ') : null),
        evaluated: !!meta.ml_evaluated_at,
    };

    const nextDue = meta.next_inspection_due || null;
    const inspOverdueDays = nextDue ? Math.floor((now - new Date(nextDue).getTime()) / DAY_MS) : null;
    const hasGeometry = !!prediction.hasGeometry;
    const openWo = Math.max(num(wo.open) ?? 0, num(road?.open_work_orders_count) ?? 0);

    // ── AI priority: AI risk index + what the segment ML and field data add ──
    const reasons = [];
    let score = prediction.riskScore;
    if (prediction.drivers?.[0]) reasons.push({ points: prediction.drivers[0].points, label: `AI: ${prediction.drivers[0].label}` });
    const add = (points, label) => { score += points; reasons.push({ points, label }); };
    if (s) {
        if (seg.poor_segments > 0) add(Math.min(15, 5 * seg.poor_segments), `${seg.poor_segments} segment(s) below PCI 50`);
        if (seg.min_pci != null && seg.min_pci < 35) add(8, `worst segment PCI ${seg.min_pci}`);
        if (seg.potholes >= 3) add(Math.min(8, seg.potholes), `${seg.potholes} potholes`);
        if (wo.critical_open > 0) add(Math.min(12, 6 * wo.critical_open), `${wo.critical_open} critical open job(s)`);
        if (wo.overdue > 0) add(Math.min(10, 4 * wo.overdue), `${wo.overdue} overdue job(s)`);
        if (wo.qa_pending > 0) add(4, `${wo.qa_pending} paving job(s) without QA`);
        if (qa.latest_status === 'rework_mandated') add(6, 'last QA audit: rework mandated');
        if (insp.last_score != null && insp.last_score < 55) add(6, `last inspection ${insp.last_score}/100`);
    }
    score = Math.max(0, Math.min(100, Math.round(score)));
    reasons.sort((a, b) => b.points - a.points);
    const level = riskLevel(score);

    // ── next best action (first match wins) ──
    let next;
    if (wo.overdue > 0) next = { intent: REPORT_INTENTS.OPEN_REPORT, label: `Clear ${wo.overdue} overdue job(s)` };
    else if (seg.poor_segments > 0 && seg.worst_segment_id) next = { intent: REPORT_INTENTS.OPEN_REPORT, label: `Paving WO for ${seg.worst_segment_id} (PCI ${seg.min_pci})` };
    else if (wo.qa_pending > 0) next = { intent: REPORT_INTENTS.OPEN_REPORT, label: `QA audit ${wo.qa_pending} paving job(s)` };
    else if (!hasGeometry) next = { intent: REPORT_INTENTS.MAP, label: 'Draw on GIS map to segment' };
    else if (s && (seg.count === 0 || seg.unscored_segments > 0 || !ml.evaluated)) next = { intent: REPORT_INTENTS.OPEN_REPORT, label: 'Run ML segment evaluation' };
    else if (s && insp.submitted === 0) next = { intent: REPORT_INTENTS.OPEN_REPORT, label: 'Record a tick-box inspection' };
    else if (inspOverdueDays != null && inspOverdueDays > 0) next = { intent: REPORT_INTENTS.OPEN_REPORT, label: `Inspection ${inspOverdueDays}d overdue` };
    else if (prediction.source !== 'model') next = { intent: REPORT_INTENTS.RUN_AI, label: 'Run AI life prediction' };
    else next = { intent: REPORT_INTENTS.OPEN_REPORT, label: prediction.actions?.[0]?.label || 'Routine monitoring' };

    return {
        id: road?.id,
        road,
        name: road?.name || 'Unnamed Road',
        code: meta.road_code || null,
        constituency: meta.constituency || null,
        status: road?.status || 'unknown',
        condition: { rating: meta.condition_rating || null, score: num(meta.condition_score) },
        hasGeometry,
        lengthM: num(meta.length_m) ?? (seg.total_length_m || null),
        segments: seg,
        ml,
        ai: {
            source: prediction.source,
            rulYears: prediction.rulYears,
            band: prediction.band,
            riskScore: prediction.riskScore,
            riskLevel: prediction.riskLevel,
            live: prediction.live,
            floodAlert: prediction.floodAlert,
            ponding: prediction.ponding || null, // --- UPDATE (roads v1.4)
            insights: prediction.insights?.length || 0,
            summary: prediction.summary,
        },
        workOrders: { ...wo, open: openWo },
        qa,
        inspections: { ...insp, nextDue, overdueDays: inspOverdueDays != null && inspOverdueDays > 0 ? inspOverdueDays : null },
        priority: { score, level, reasons: reasons.slice(0, 5), next },
        summaryLoaded: !!s,
    };
}

/** Page-level KPIs for the table header. */
export function summarizeReportRows(rows = []) {
    let segments = 0; let pciWeight = 0; let pciSum = 0; let attention = 0; let open = 0; let qaPending = 0; let unsegmented = 0; let mlEvaluated = 0;
    rows.forEach((r) => {
        segments += r.segments.count || 0;
        if (r.segments.avg_pci != null && r.segments.count) { pciWeight += r.segments.count; pciSum += r.segments.avg_pci * r.segments.count; }
        if (r.priority.level.key === 'critical' || r.priority.level.key === 'high') attention += 1;
        open += r.workOrders.open || 0;
        qaPending += r.workOrders.qa_pending || 0;
        if (r.summaryLoaded && !r.segments.count) unsegmented += 1;
        if (r.ml.evaluated) mlEvaluated += 1;
    });
    return {
        roads: rows.length,
        segments,
        avgPci: pciWeight ? Math.round((pciSum / pciWeight) * 10) / 10 : null,
        attention, open, qaPending, unsegmented, mlEvaluated,
    };
}

export const REPORT_FILTERS = Object.freeze({
    all: { label: 'All', test: () => true },
    attention: { label: 'Needs attention', test: (r) => r.priority.level.key === 'critical' || r.priority.level.key === 'high' },
    unsegmented: { label: 'Not segmented', test: (r) => r.summaryLoaded && !r.segments.count },
    no_ml: { label: 'No ML run', test: (r) => !r.ml.evaluated },
    qa_pending: { label: 'QA pending', test: (r) => r.workOrders.qa_pending > 0 },
});

export const REPORT_SORTS = Object.freeze({
    priority: (r) => r.priority.score,
    name: (r) => r.name.toLowerCase(),
    pci: (r) => (r.ml.pci ?? 101),
    segments: (r) => r.segments.count,
    work_orders: (r) => r.workOrders.open,
    rul: (r) => (r.ai.rulYears ?? 999),
});

export function sortReportRows(rows, key = 'priority', dir = 'desc') {
    const get = REPORT_SORTS[key] || REPORT_SORTS.priority;
    const m = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const x = get(a); const y = get(b);
        if (x < y) return -1 * m;
        if (x > y) return 1 * m;
        return String(a.name).localeCompare(String(b.name));
    });
}

const csvCell = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** CSV of the report table rows (what the user currently sees). */
export function reportRowsToCsv(rows = []) {
    const head = ['road_id', 'name', 'code', 'constituency', 'status', 'condition_rating', 'condition_score',
        'segments', 'length_m', 'potholes', 'poor_segments', 'ml_pci', 'worst_segment', 'worst_pci', 'ml_engine', 'ml_action', 'ml_evaluated_at',
        'ai_rul_years', 'ai_source', 'ai_risk', 'ai_priority', 'priority_level', 'next_action',
        'open_wos', 'critical_open', 'overdue', 'qa_pending', 'qa_latest', 'last_inspection_score', 'last_inspection_at', 'next_inspection_due',
        'ponding_band', 'ponding_max_depth_m', 'ponding_segments']; // --- UPDATE (roads v1.4)
    const lines = rows.map((r) => [
        r.id, r.name, r.code, r.constituency, r.status, r.condition.rating, r.condition.score,
        r.segments.count, r.lengthM, r.segments.potholes, r.segments.poor_segments, r.ml.pci, r.segments.worst_segment_id, r.segments.min_pci, r.ml.engine, r.ml.action, r.ml.evaluatedAt,
        r.ai.rulYears, r.ai.source, r.ai.riskScore, r.priority.score, r.priority.level.label, r.priority.next.label,
        r.workOrders.open, r.workOrders.critical_open, r.workOrders.overdue, r.workOrders.qa_pending, r.qa.latest_status, r.inspections.last_score, r.inspections.last_submitted_at, r.inspections.nextDue,
        r.ai.ponding?.band ?? '', r.ai.ponding?.max_depth_m ?? '', r.ai.ponding?.ponding_segments ?? '', // --- UPDATE (roads v1.4)
    ].map(csvCell).join(','));
    return [head.join(','), ...lines].join('\n');
}
